/**
 * W12 — WebSocket 客户端与消息分发
 *
 * 后端消息格式（来自 serializer.py / main.py）：
 *   {"type": "output",   "event": "Stdout"|"Stderr", "data": {"message": "..."}}
 *   {"type": "graphics", "event": "<EventName>",     "data": {...}}
 *   {"type": "control",  "event": "Done",            "data": {}}
 *   {"type": "control",  "event": "Error",           "data": {"message": "..."}}
 */

import { appendOutput, appendError, setRunning } from './output.js';
import { getMessages } from './i18n.js';
import { getLocale } from './locale.js';

// ── 内部状态 ──────────────────────────────────────────────────────────────────
let socket    = null;
let sessionId = null;
let _onReady  = null;   // 保存重连时使用
let rendererModulePromise = null;

function t() {
  return getMessages('pages/workspace', getLocale());
}

function loadRenderer() {
  rendererModulePromise ??= import('./renderer.js');
  return rendererModulePromise;
}

function preloadRenderer() {
  void loadRenderer().catch((error) => {
    console.warn('[TianYuan] 图形渲染器预加载失败:', error);
  });
}

// ── 连接 ──────────────────────────────────────────────────────────────────────
/**
 * 建立（或重建）WebSocket 连接。
 * session_id 持久化到 sessionStorage，同一标签页刷新后保留工作区状态，
 * 不同标签页各自独立，避免多标签共用同一后端 Session。
 * @param {Function} [onReady]  连接建立后的回调
 */
export function connect(onReady) {
  _onReady  = onReady ?? _onReady;
  sessionId = sessionStorage.getItem('ty_session_id') || crypto.randomUUID();
  sessionStorage.setItem('ty_session_id', sessionId);

  const wsUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${sessionId}`;
  socket = new WebSocket(wsUrl);

  socket.onopen = () => {
    console.log('[TianYuan] WebSocket 已连接，session:', sessionId);
    preloadRenderer();
    _onReady?.();
  };

  socket.onmessage = async (ev) => {
    try {
      await handleMessage(JSON.parse(ev.data));
    } catch (e) {
      console.error('[TianYuan] 无法解析消息:', ev.data, e);
    }
  };

  socket.onerror = () => {
    appendError(t().websocketError);
  };

  socket.onclose = () => {
    console.log('[TianYuan] WebSocket 断开，5 秒后重连…');
    setRunning(false);
    setTimeout(() => connect(), 5000);
  };
}

// ── 消息分发 ──────────────────────────────────────────────────────────────────
async function dispatchGraphics(event, data) {
  const renderer = await loadRenderer();
  renderer.dispatch(event, data);
}

async function clearAllFiguresIfReady() {
  if (!rendererModulePromise) return;
  const renderer = await loadRenderer();
  renderer.prepareFiguresForExecute();
}

async function handleMessage(msg) {
  switch (msg.type) {
    case 'output':
      // Stdout / Stderr
      if (msg.event === 'Stderr') {
        appendError(msg.data?.message ?? '');
      } else {
        appendOutput(msg.data?.message ?? '');
      }
      break;

    case 'graphics':
      await dispatchGraphics(msg.event, msg.data);
      break;

    case 'graphics_bulk':
      // 批量图形事件：一次性处理所有事件，前端只执行一次 ECharts setOption
      {
        const renderer = await loadRenderer();
        renderer.processBatch(msg.events || []);
      }
      break;

    case 'error':
      // 后端 RuntimeError（type:"error"，区别于 type:"control" 的 Error 事件）
      appendError(msg.data?.message ?? t().executionError);
      setRunning(false);
      break;

    case 'control':
      if (msg.event === 'Error') {
        appendError(msg.data?.message ?? t().executionError);
        setRunning(false);
      } else if (msg.event === 'Done') {
        setRunning(false);
        document.dispatchEvent(new CustomEvent('ty:done'));
      } else if (msg.event === 'SaveFigure') {
        // SaveFigure 由 serializer 归类为 control，转发给 renderer
        await dispatchGraphics('SaveFigure', msg.data);
      }
      break;

    default:
      console.warn('[TianYuan] 未知消息类型:', msg.type);
  }
}

// ── 对外接口 ──────────────────────────────────────────────────────────────────
/**
 * 向服务端发送 execute 消息。
 * @param {string} code
 */
export function execute(code) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    appendError(t().websocketNotConnected);
    return;
  }
  void clearAllFiguresIfReady();
  setRunning(true);
  socket.send(JSON.stringify({ type: 'execute', code }));
}

/**
 * 向服务端发送 interrupt 消息（中断当前执行）。
 */
export function interrupt() {
  socket?.send(JSON.stringify({ type: 'interrupt' }));
}

/**
 * 向服务端发送 clear 消息（清空工作区变量）。
 */
export function clearWorkspace() {
  socket?.send(JSON.stringify({ type: 'clear' }));
}

/** 返回当前 session_id。 */
export function getSessionId() {
  return sessionId;
}

/** 返回连接是否已就绪。 */
export function isConnected() {
  return socket !== null && socket.readyState === WebSocket.OPEN;
}
